import {
  Box,
  CircularProgress,
  Dialog,
  Fade,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import logo from "assets/logos/logo-transparent.png";
import { OrganizationUserContext } from "contexts/Organization";
import { SplashPage } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Plans } from "schemas/payments";
import { routes } from "schemas/routes";

import AuthForm from "components/Auth";
import { B2GO_PROMO_END, B2GO_PROMO_START } from "constants/influencer";
import { PAGE_VISITED } from "constants/trackingProps";
import { AuthAction } from "features/Auth/schema";
import { getB2GOEffectivePlan } from "features/Home/UpgradeDialog/Plan/helpers";
import { getParams, getSource, trackEvent } from "utils/tracking";
import { useLogin } from "utils/useLogin";
import { useSubscription } from "utils/useSubscription";

import Hero from "./Hero";
import B2GoPlans from "./Plans";
import { getSalePhase } from "./helpers";
import styles from "./styles";

const PENDING_PLAN_KEY = "b2go_pending_plan";

const B2GoSplashPage = () => {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const source = getSource();
  const navigate = useNavigate();
  const location = useLocation();
  const { fetchUserInfo, fetchUserLoading } = useLogin();
  const { currentOrg } = useContext(OrganizationUserContext);
  const isSignedIn = getParams("signed_in") === "true";
  const { handleCheckout } = useSubscription();
  const fromRoutes = `/${routes.b2go}`;

  const phase = getSalePhase();
  const isLive = phase === "live";
  const isPaidUser = Boolean(currentOrg?.isPaidUser);
  const canSubscribe = isLive && !isPaidUser;
  const countdownTarget = phase === "pre" ? B2GO_PROMO_START : B2GO_PROMO_END;

  let ctaLabel = "Offer ended";
  let subNote = "The free-month promotion has ended.";
  if (isLive) {
    ctaLabel = "Subscribe now";
    subNote = "Cancel anytime · renews at the regular price.";
  } else if (phase === "pre") {
    ctaLabel = "Opens June 29";
    subNote = "Subscriptions open June 29, 2026.";
  }
  if (isPaidUser) {
    ctaLabel = "Only for new users";
    subNote = "This offer is only available for new users.";
  }

  const [displayLoad, setDisplayLoad] = useState(false);

  const isAuthRoute =
    location.pathname.includes("/sign-in") ||
    location.pathname.includes("/sign-up");

  const redirectUrl = `/${routes.b2go}?signed_in=true`;

  const closeDialog = () => {
    navigate(`/${routes.b2go}${location.search}`, { replace: true });
  };

  const checkOutForSplashPage = (selectedPlan: Plans) => {
    if (currentOrg?.isPaidUser) {
      return;
    }
    setDisplayLoad(true);
    if (!currentOrg) {
      sessionStorage.setItem(PENDING_PLAN_KEY, selectedPlan);
      navigate(`/${routes.b2go}/sign-up${location.search}`);
      return;
    }
    sessionStorage.removeItem(PENDING_PLAN_KEY);

    handleCheckout(
      getB2GOEffectivePlan(selectedPlan, currentOrg.isPaidUser),
      source || "",
      window.location.pathname,
      SplashPage.b2go,
    );
  };

  useEffect(() => {
    trackEvent(PAGE_VISITED);
    if (!currentOrg?.id) {
      fetchUserInfo();
      return;
    }
    const pendingPlan = sessionStorage.getItem(
      PENDING_PLAN_KEY,
    ) as Plans | null;
    if (isSignedIn && pendingPlan) {
      checkOutForSplashPage(pendingPlan);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, currentOrg?.id]);

  return (
    <Box sx={styles.page}>
      <Box sx={styles.inner}>
        <Box
          component="a"
          href="https://www.onbento.com/?s=b2go"
          sx={styles.logoWrap}
        >
          <Box component="img" src={logo} alt="Bento" sx={styles.logoImg} />
        </Box>

        <Hero phase={phase} countdownTarget={countdownTarget} />

        <B2GoPlans
          canSubscribe={canSubscribe}
          ctaLabel={ctaLabel}
          subNote={subNote}
          onSubscribe={checkOutForSplashPage}
        />

        <Typography sx={styles.footer}>
          The free month applies to your third billing cycle on any monthly paid
          plan.
          <br />
          Questions about the offer?{" "}
          <Box
            component="a"
            href="mailto:hello@onbento.com"
            sx={styles.footerLink}
          >
            hello@onbento.com
          </Box>
        </Typography>

        <Box sx={styles.freePlanWrap}>
          <Typography
            component="a"
            href="/sign-up?s=b2go"
            sx={styles.freePlanLink}
          >
            Want to try it for free first? 👀
          </Typography>
        </Box>
      </Box>

      <Fade in={fetchUserLoading && displayLoad} unmountOnExit>
        <Box sx={styles.overlay}>
          <Box sx={styles.overlayCard}>
            <CircularProgress
              size={44}
              thickness={3}
              sx={{ color: "#F7EE6F" }}
            />
            <Typography sx={styles.overlayTitle}>Setting things up…</Typography>
            <Typography sx={styles.overlaySub}>
              Just a moment while we prepare your checkout.
            </Typography>
          </Box>
        </Box>
      </Fade>

      <Dialog
        open={isAuthRoute && !fetchUserLoading}
        onClose={closeDialog}
        fullScreen={isMobileScreen}
        PaperProps={{
          sx: {
            borderRadius: isMobileScreen ? 0 : "16px",
            overflow: "hidden",
            width: "fit-content",
            px: 2,
            minWidth: 250,
          },
        }}
      >
        <Box sx={styles.dialogClose}>
          <IconButton onClick={closeDialog} size="small">
            <Box component="i" className="fa-regular fa-xmark" />
          </IconButton>
        </Box>

        <Routes>
          <Route
            path="/sign-in/*"
            element={
              <AuthForm
                redirectUrl={redirectUrl}
                action={AuthAction.SIGN_IN}
                signInUrl={`${fromRoutes}/sign-in${location.search}`}
                signUpUrl={`${fromRoutes}/sign-up${location.search}`}
                fromRoutes={fromRoutes}
                isDialog
              />
            }
          />
          <Route
            path="/sign-up/*"
            element={
              <AuthForm
                redirectUrl={redirectUrl}
                action={AuthAction.SIGN_UP}
                signInUrl={`${fromRoutes}/sign-in${location.search}`}
                signUpUrl={`${fromRoutes}/sign-up${location.search}`}
                fromRoutes={fromRoutes}
                isDialog
              />
            }
          />
        </Routes>
      </Dialog>
    </Box>
  );
};

export default B2GoSplashPage;
